import { Table, Tbody, Tr, Td } from '@components/core-ui/Table/Table';
import { formatDuration } from '@components/FileUpload/components/dropzone';
import { TFileWithMeta } from '@components/FileUpload/components/dropzone/types/TFileWithMeta';
import { Loader } from '@components/FileUpload/components/Loader';
import { PreviewFile } from '@components/FileUpload/components/PreviewFile';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { UploadObjectType } from 'app/types/enums/uploadObjectType';
import { useAPI } from '@providers/useAPI';
import { mapAttachmentsToIFileWithMeta } from '@pages/User/RepositoryPage/helpers/mapAttachmentsToIFileWithMeta';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import {
  BulkCreateJobFromRepositoryResponseDto,
  JobsService,
  MediaService,
  PayService,
  RepositoriesService,
} from 'app/API';
import { TAudioFile } from 'app/types/entities/TAudioFile';
import { TRepository } from 'app/types/entities/TRepository';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './BulkSubmitJobsModal/styles/Content';
import { StyledLink } from './BulkSubmitJobsModal/styles/StyledLink';

type TJobWithCreatedMedia = BulkCreateJobFromRepositoryResponseDto & { analysis?: string };

type TProps = {
  repository: TRepository;
  audioFiles: TAudioFile[];
  organizationId: string;
  repositoryId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

enum BulkJobStep {
  GetAudioDuration = 'retrieving_audio_duration',
  RetrievingPrice = 'retrieving_price',
  RetrievedPrice = 'retrieved_price',
  CreatingDraftJobs = 'creating_draft_jobs',
  DraftJobsCreated = 'draft_jobs_created',
  JobsSubmittedSuccessfully = 'jobs_submitted_successfully',
  JobsSubmittedWithErrors = 'jobs_submitted_with_errors',
}

type RepositoryBulkJobCreationButtonLabel = 'Create Jobs' | 'Submit Jobs' | 'Close';

export const RepositoryBulkJobCreationModal = ({ repositoryId, organizationId, onCancel }: TProps) => {
  const [isSubmitting, setIsSubmitting] = useState(true);
  const [isAnalyzingAudio, setIsAnalyzingAudio] = useState(true);
  const [step, setStep] = useState(BulkJobStep.GetAudioDuration);
  const [totalAudioDuration, setTotalAudioDuration] = useState<number>();
  const [buttonLabel, setButtonLabel] = useState<RepositoryBulkJobCreationButtonLabel>(
    'Create Jobs' as RepositoryBulkJobCreationButtonLabel,
  );
  const [createdJobs, setCreatedJobs] = useState<TJobWithCreatedMedia[]>([]);
  const [audioFiles, setAudioFiles] = useState<TFileWithMeta[]>([]);
  const { call } = useAPI();

  const { me, workspace, organization } = useAuthContext();

  const billingAccount = me?.organizations?.find((o) => o.id === organizationId)?.billingAccount;

  const totalPrice = totalAudioDuration ? (totalAudioDuration / 3600) * (Number(billingAccount?.tierRate) || 0) : 0;

  const pollAnalysisResult = (mediaId: string) => {
    let analysisChecksCount = 0;
    const interval = setInterval(async () => {
      const analysisResponse = await call(MediaService.getAnalysisStatus({ mediaId }));

      if (!analysisResponse) {
        clearInterval(interval);
      }

      if (analysisResponse.returnvalue) {
        setCreatedJobs((prev) =>
          prev.map((createdJob) => {
            if (createdJob.createdMedia.id === mediaId) {
              return {
                ...createdJob,
                createdMedia: {
                  ...createdJob.createdMedia,
                  duration: Number(analysisResponse.returnvalue?.duration || 0),
                },
                analysis: 'complete',
              };
            }
            return createdJob;
          }),
        );
        clearInterval(interval);
        return;
      }

      analysisChecksCount++;

      if (analysisChecksCount >= 60 || analysisResponse.failedReason) {
        setCreatedJobs((prev: TJobWithCreatedMedia[]) =>
          prev.map((createdJob: TJobWithCreatedMedia) => {
            if (createdJob.createdMedia.id === mediaId) {
              return { ...createdJob, analysis: 'error' };
            }
            return createdJob;
          }),
        );
        clearInterval(interval);
        return;
      }
    }, 5000);
  };

  useEffect(() => {
    const isAnalysisInProcessing = createdJobs.some((job) => !job.analysis);
    setIsAnalyzingAudio(isAnalysisInProcessing);

    if (!isAnalysisInProcessing) {
      setTotalAudioDuration(createdJobs.reduce((acc, job) => acc + job.createdMedia.duration, 0));
    }
  }, [createdJobs]);

  useEffect(() => {
    async function setup() {
      try {
        const audioFiles = await call(() => RepositoriesService.findAllAudioAttachments({ repositoryId }), {
          onError: (error: string) => {
            toast.error(error);
          },
          dontRedirectOnError: true,
        });
        setAudioFiles(mapAttachmentsToIFileWithMeta(audioFiles));

        const totalDuration = audioFiles
          .map((a) => a.audioDuration || 0)
          .reduce((acc: number, curr: number) => acc + curr, 0);

        setTotalAudioDuration(totalDuration);
        setStep(BulkJobStep.RetrievedPrice);
      } catch {
        onCancel();
      } finally {
        setIsSubmitting(false);
      }
    }

    if (step === BulkJobStep.GetAudioDuration) {
      setup();
    }
  }, [repositoryId, step]);

  const draftJobCreationHandler = async () => {
    if (!workspace?.id) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await call(
        () =>
          JobsService.bulkCreateRepositoryJobs({
            repositoryId,
            requestBody: { workspaceId: workspace?.id },
          }),
        {
          onError: (error: string) => {
            toast.error(error);
          },
          dontRedirectOnError: true,
        },
      );

      setCreatedJobs(data);
      data.forEach((job) => {
        pollAnalysisResult(job.createdMedia.id);
      });

      setStep(BulkJobStep.DraftJobsCreated);
      setButtonLabel('Submit Jobs');
    } catch {
      onCancel();
    } finally {
      setIsSubmitting(false);
    }
  };

  const jobSubmissionHandler = async () => {
    setIsSubmitting(true);

    try {
      await call(
        () =>
          PayService.payForBulkJobs({
            requestBody: {
              ids: createdJobs
                .sort((job, nextJob) => Number(job.createdMedia.duration) - Number(nextJob.createdMedia.duration))
                .map((job) => job.id),
            },
          }),
        {
          onError: (error: string) => {
            setStep(BulkJobStep.JobsSubmittedWithErrors);
            toast.error(error);
          },
          onSuccess: () => {
            setStep(BulkJobStep.JobsSubmittedSuccessfully);
          },
        },
      );
    } finally {
      setIsSubmitting(false);
      setButtonLabel('Close');
    }
  };

  const onRemoveFile = (file: TFileWithMeta) => {
    const removeFilter = (f: TFileWithMeta) => f.meta.Id !== file.meta.Id;
    setAudioFiles([...audioFiles.filter(removeFilter)]);
  };

  return (
    <ModalWrapper width="700px">
      <ModalTitle>Bulk Job Creation</ModalTitle>
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <Content>
        <div>
          {step === BulkJobStep.GetAudioDuration && <div>Retrieving audio duration...</div>}

          {step === BulkJobStep.RetrievedPrice && audioFiles.length === 0 && <div>No audio files found</div>}

          {step === BulkJobStep.RetrievedPrice &&
            audioFiles.map((a) => (
              <PreviewFile
                key={a.meta.Id}
                fileWithMeta={a}
                objectType={UploadObjectType.REPOSITORY}
                hideDownloadButton={true}
                hideRemoveButton={true}
                onRemoveFile={onRemoveFile}
              />
            ))}

          {step === BulkJobStep.DraftJobsCreated ? (
            <div>
              <p className="text-[var(--color-text-muted)] font-[600] text-left text-sm pb-2">Draft jobs created</p>

              {isAnalyzingAudio ? (
                <p className="text-[var(--color-text-muted)] font-[600] text-left text-sm pb-2">Analyzing audio...</p>
              ) : null}

              {createdJobs.map((job) => (
                <div className="flex my-2 items-center text-sm" key={job.id}>
                  <StyledLink
                    to={routes.jobEdit.make(organization?.slug ?? '', workspace?.slug ?? '', job.id)}
                    className="mr-2 max-w-full text-ellipsis overflow-hidden"
                  >
                    {job.name}
                  </StyledLink>

                  {!job.analysis ? (
                    <Loader size={14} />
                  ) : job.analysis === 'complete' ? (
                    <div className="flex items-center text-nowrap">
                      <div className="mr-2">[{formatDuration(job.createdMedia.duration)},</div>
                      <div className="text-sm font-[600] text-right py-2">
                        ${((job.createdMedia.duration / 3600) * (Number(billingAccount?.tierRate) || 0)).toFixed(2)}
                      </div>
                      ]
                    </div>
                  ) : (
                    <div className="text-red-500" style={{ fontSize: '0.775rem' }}>
                      Analysis error
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null}

          {step === BulkJobStep.JobsSubmittedSuccessfully ? <div>Jobs submitted successfully</div> : null}

          {step === BulkJobStep.JobsSubmittedWithErrors ? (
            <div className="text-red-500">Jobs submitted with errors, please check job list</div>
          ) : null}
        </div>

        {audioFiles.length > 0 ? (
          <Table className="text-left text-xs border-collapse">
            <Tbody>
              <Tr className="border-b border-solid border-[var(--color-divider)]">
                <Td className="text-[var(--color-text-muted)] font-[600] py-2">Total duration</Td>
                <Td className="text-sm text-right py-2">
                  {totalAudioDuration ? formatDuration(totalAudioDuration) : <i>Loading...</i>}
                </Td>
              </Tr>
              <Tr>
                <Td className="text-[var(--color-text-muted)] font-[600] py-2">Total price for bulk job</Td>
                <Td className="text-sm font-[600] text-right py-2">${totalPrice.toFixed(2)}</Td>
              </Tr>
            </Tbody>
          </Table>
        ) : null}

        <div className="buttonContainer">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button
            className="ml-4"
            onClick={() => {
              if (step === BulkJobStep.RetrievedPrice) {
                return draftJobCreationHandler();
              }

              if (step === BulkJobStep.DraftJobsCreated) {
                return jobSubmissionHandler();
              }

              return onCancel();
            }}
            disabled={isSubmitting || (buttonLabel === 'Submit Jobs' && isAnalyzingAudio)}
          >
            {buttonLabel}
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
